/**
 * Tag manager for creating and deleting tags.
 */
import React, { useState } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button, Input, TrashIcon } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useCreateTag, useDeleteTag } from '@/hooks/queries/useMediaQueries';
import type { MediaTag } from '@/types/media';

interface TagManagerProps {
  isOpen: boolean;
  onClose: () => void;
  tags: MediaTag[];
}

export function TagManager({ isOpen, onClose, tags }: TagManagerProps) {
  const [newTagName, setNewTagName] = useState('');
  const [deleteTagId, setDeleteTagId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const createMutation = useCreateTag();
  const deleteMutation = useDeleteTag();

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newTagName.trim()) {
      setError('Tag name is required');
      return;
    }

    // Check for duplicates
    if (tags.some((t) => t.name.toLowerCase() === newTagName.trim().toLowerCase())) {
      setError('A tag with this name already exists');
      return;
    }

    try {
      await createMutation.mutateAsync({ name: newTagName.trim() });
      setNewTagName('');
    } catch (error) {
      console.error('TagManager: Failed to create tag:', error);
      setError('Failed to create tag');
    }
  };

  const handleDeleteTag = async () => {
    if (!deleteTagId) return;
    await deleteMutation.mutateAsync(deleteTagId);
    setDeleteTagId(null);
  };

  const tagToDelete = tags.find((t) => t.id === deleteTagId);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Manage Tags</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Create tag form */}
              <form onSubmit={handleCreateTag} className="flex gap-2">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="New tag name"
                  className="flex-1"
                />
                <Button type="submit" disabled={createMutation.isPending}>
                  <PlusIcon className="w-4 h-4" />
                </Button>
              </form>
              {error && <p className="text-sm text-red-600">{error}</p>}

              {/* Tag list */}
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {tags.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">
                    No tags yet. Create one above.
                  </p>
                ) : (
                  tags.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex items-center justify-between p-3"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="text-sm font-medium">{tag.name}</span>
                        <span className="text-xs text-gray-400">
                          ({tag.image_count} images)
                        </span>
                      </div>
                      <button
                        onClick={() => setDeleteTagId(tag.id)}
                        className="text-gray-400 hover:text-red-500 p-1"
                        title="Delete tag"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <p className="text-xs text-gray-500">
                Colors are automatically assigned from a preset palette.
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-4 border-t bg-gray-50">
              <Button variant="secondary" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTagId}
        onClose={() => setDeleteTagId(null)}
        onConfirm={handleDeleteTag}
        title="Delete Tag"
        message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This will remove the tag from all images.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
